import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Input } from '../components/Input';
import { CheckCircle2, ArrowLeft, X, Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveApiBaseUrl } from '../lib/utils';

type AuthVariant = 'page' | 'modal'

interface ResetPasswordScreenProps {
  variant?: AuthVariant
  onClose?: () => void
  onBack?: () => void
  onComplete?: () => void
}

export const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({
  variant = 'page',
  onClose,
  onBack,
  onComplete
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [contextError, setContextError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetContext, setResetContext] = useState<{ phone: string; code: string } | null>(null);

  const isModal = variant === 'modal'

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') {
      setContextError(t('resetContextMissing'))
      return
    }
    const clearContext = () => {
      try {
        sessionStorage.removeItem('auth_reset_context')
      } catch (error) {
        // ignore storage errors
      }
    }
    const raw = sessionStorage.getItem('auth_reset_context')
    if (!raw) {
      setContextError(t('resetContextMissing'))
      return
    }
    try {
      const parsed = JSON.parse(raw)
      if (!parsed?.phone || !parsed?.code) {
        clearContext()
        setContextError(t('resetContextMissing'))
        return
      }
      if (parsed?.created_at && Date.now() - Number(parsed.created_at) > 10 * 60 * 1000) {
        clearContext()
        setContextError(t('resetContextMissing'))
        return
      }
      setResetContext({ phone: parsed.phone, code: parsed.code })
      setContextError('')
    } catch (error) {
      clearContext()
      setContextError(t('resetContextMissing'))
    }
  }, [t])

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    navigate('/login')
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmitting || isSuccess) return
    if (!resetContext || contextError) {
      setErrorMessage(contextError || t('resetContextMissing'))
      return
    }
    if (password.length < 8) {
      setErrorMessage(t('resetRequirement'))
      return
    }
    if (password !== confirmPassword) {
      setErrorMessage(t('resetMismatch'))
      return
    }
    setErrorMessage('')
    setIsSubmitting(true)
    try {
      const apiBaseUrl = resolveApiBaseUrl()
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: resetContext.phone,
          code: resetContext.code,
          new_password: password
        })
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        setErrorMessage(payload?.detail || t('resetFailed'))
        setIsSubmitting(false)
        return
      }
      try {
        sessionStorage.removeItem('auth_reset_context')
      } catch (error) {
        // ignore storage errors
      }
      setIsSubmitting(false)
      setIsSuccess(true)
      window.setTimeout(() => {
        if (onComplete) {
          onComplete()
          return
        }
        navigate('/login')
      }, 1200)
    } catch (error) {
      setErrorMessage(t('resetFailed'))
      setIsSubmitting(false)
    }
  }

  const strengthScore = useMemo(() => {
    let score = 0
    if (password.length >= 8) score += 1
    if (/[A-Z]/.test(password) || /[a-z]/.test(password)) score += 1
    if (/\d/.test(password)) score += 1
    if (/[^A-Za-z0-9]/.test(password)) score += 1
    return Math.min(score, 4)
  }, [password])

  const strengthLabel = useMemo(() => {
    if (strengthScore >= 3) return t('resetStrengthStrong')
    if (strengthScore >= 2) return t('resetStrengthMedium')
    return t('resetStrengthWeak')
  }, [strengthScore, t])

  const strengthText = t('resetStrengthLabel').replace('{level}', strengthLabel)

  const steps = useMemo(() => [t('resetStep1'), t('resetStep2'), t('resetStep3')], [t])
  const activeStep = 2

  const isDisabled = isSubmitting || isSuccess || !resetContext || !!contextError

  return (
    <div
      data-auth-variant={variant}
      className={isModal ? 'w-full' : 'min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4'}
    >
    <div
      className={`relative w-full bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 p-10 md:p-12 ${
        isModal ? '' : 'max-w-[440px]'
      }`}
    >
      {isModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
      <div className="flex flex-col items-center text-center mb-8">
        <div className="mb-6">
          <Logo />
        </div>

        <div className="flex items-center justify-center gap-3 mb-6">
          {steps.map((label, index) => (
            <div key={label} className="flex items-center gap-3">
              <div
                className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${
                  index <= activeStep ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'
                }`}
              >
                {index + 1}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${index === activeStep ? 'text-slate-900' : 'text-slate-400'}`}>
                {label}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-8 h-px ${index < activeStep ? 'bg-primary' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-success/5 border border-success/20 px-4 py-1.5 rounded-full mb-6">
          <CheckCircle2 size={16} className="text-success" strokeWidth={2.5} />
          <span className="text-xs font-bold text-success uppercase tracking-wider">{t('phoneVerified')}</span>
        </div>

        <h1 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">{t('setNewPass')}</h1>
        <p className="text-slate-500 text-sm leading-relaxed max-w-[280px]">
          {t('newPassSubtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {contextError && (
          <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
            <AlertCircle size={14} />
            <span className="text-xs font-medium">{contextError}</span>
          </div>
        )}
        <div className="space-y-2">
           <Input
             label={t('newPassLabel')}
             type="password"
             placeholder={t('newPassPlaceholder')}
             value={password}
             onChange={(event) => {
               setPassword(event.target.value)
               if (errorMessage) setErrorMessage('')
             }}
             disabled={isDisabled}
             error={errorMessage}
           />
           {/* Password strength visual */}
           <div className="flex gap-1.5 mt-2">
             {[0, 1, 2, 3].map((index) => (
               <div
                 key={index}
                 className={`h-1 flex-1 rounded-full ${index < strengthScore ? 'bg-success' : 'bg-slate-100'}`}
               />
             ))}
           </div>
           <p className="text-[11px] text-slate-400 font-medium text-right">{strengthText}</p>
           {!isSuccess && (
             <p className="text-[11px] text-slate-400 font-medium">{t('resetRequirement')}</p>
           )}
        </div>

        <div className="space-y-2">
           <Input
             label={t('confirmPassLabel')}
             type="password"
             placeholder={t('confirmPassPlaceholder')}
             value={confirmPassword}
             onChange={(event) => {
               setConfirmPassword(event.target.value)
               if (errorMessage) setErrorMessage('')
             }}
             disabled={isDisabled}
             error={errorMessage && password !== confirmPassword ? errorMessage : ''}
           />
        </div>

        <button
          type="submit"
          disabled={isDisabled}
          className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 active:scale-[0.98] mt-2 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : t('updatePass')}
        </button>

        {isSuccess && (
          <div className="flex items-center justify-center gap-2 text-success bg-success/5 border border-success/20 rounded-xl py-3 text-sm font-semibold">
            <CheckCircle2 size={18} strokeWidth={2.5} />
            <span>{t('resetSuccess')}</span>
            <span className="text-[11px] text-success/70">{t('resetSuccessHint')}</span>
          </div>
        )}
      </form>

      <div className="text-center mt-10">
        <button
          onClick={handleBack}
          className="text-sm text-slate-500 font-medium hover:text-primary transition-colors flex items-center justify-center gap-2 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {t('backSignIn')}
        </button>
      </div>
    </div>
    </div>
  );
};
